import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowSquareOut, Clock, UserCircle } from '@phosphor-icons/react';
import { DetailDrawer, SeverityBadge, StatusBadge } from '../design-system/components';

const formatDate = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(date);
};

function DefinitionList({ items }) {
  return (
    <dl className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
      {items.filter(([, value]) => value !== null && value !== undefined && value !== '').map(([label, value]) => (
        <div key={label} className="grid grid-cols-[minmax(110px,0.42fr)_minmax(0,1fr)] gap-4 py-3 text-sm">
          <dt className="text-[var(--ink-500)]">{label}</dt>
          <dd className="min-w-0 font-semibold text-[var(--ink-950)]">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Section({ title, children }) {
  if (!children) return null;
  return (
    <section className="border-t border-[var(--line)] pt-5 first:border-t-0 first:pt-0">
      <h3 className="font-heading text-base font-bold text-[var(--ink-950)]">{title}</h3>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function RelatedSignals({ signals = [], onSelect }) {
  if (!signals.length) return <p className="text-sm leading-6 text-[var(--ink-600)]">No linked validated signals are recorded.</p>;
  return (
    <div className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
      {signals.map((signal) => (
        <button key={signal.id} type="button" onClick={() => onSelect({ type: 'signal', id: signal.id })} className="flex w-full items-start justify-between gap-4 py-3 text-left hover:text-[var(--ink-950)]">
          <span className="text-sm font-semibold leading-5">{signal.content || signal.summary}</span>
          <SeverityBadge value={signal.level || signal.risk_level} />
        </button>
      ))}
    </div>
  );
}

function RiskDetails({ inspector, onSelect }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <SeverityBadge value={inspector.level} />
        {inspector.public_ref && <span className="font-mono text-xs text-[var(--ink-500)]">{inspector.public_ref}</span>}
      </div>
      <p className="text-base leading-7 text-[var(--ink-900)]">{inspector.summary}</p>
      <Section title="Current state">
        <DefinitionList items={[
          ['Severity', inspector.level ?? 'UNASSESSED'],
          ['Evidence confidence', inspector.evidenceCoverage === undefined ? null : `${inspector.evidenceCoverage}%`],
          ['Owner', inspector.relatedAction?.owner || 'Not assigned'],
          ['Action state', inspector.relatedAction?.status || null],
          ['Due', inspector.relatedAction?.due_date ? formatDate(inspector.relatedAction.due_date) : null],
        ]} />
      </Section>
      {inspector.implication && <Section title="Why this matters"><p className="text-sm leading-6 text-[var(--ink-700)]">{inspector.implication}</p></Section>}
      <Section title="Validated evidence"><RelatedSignals signals={inspector.relatedSignals} onSelect={onSelect} /></Section>
      {inspector.relatedAction && (
        <Section title="Recommended next action">
          <div className="rounded-md bg-[var(--surface-sunken)] p-4">
            <div className="font-semibold text-[var(--ink-950)]">{inspector.relatedAction.title}</div>
            <p className="mt-1 text-sm leading-6 text-[var(--ink-600)]">{inspector.relatedAction.expected_impact || inspector.relatedAction.summary}</p>
            <div className="mt-3 flex items-center gap-2 text-xs text-[var(--ink-600)]"><UserCircle size={15} /> {inspector.relatedAction.owner || 'Owner not assigned'}</div>
          </div>
        </Section>
      )}
      <div className="flex flex-wrap gap-2 border-t border-[var(--line)] pt-5">
        {inspector.relatedAction && <Link to="/work?view=actions" className="tkp-btn-primary inline-flex items-center gap-2">Open action <ArrowSquareOut size={14} /></Link>}
        {inspector.relatedAssessment && <Link to="/work?view=decisions" className="tkp-btn-secondary inline-flex items-center gap-2">Review decision <ArrowSquareOut size={14} /></Link>}
        <Link to="/intelligence?view=knowledge" className="tkp-btn-secondary inline-flex items-center gap-2">Open evidence <ArrowSquareOut size={14} /></Link>
      </div>
    </>
  );
}

function SignalDetails({ inspector }) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2"><SeverityBadge value={inspector.level} /><StatusBadge value={inspector.trustStatus || 'validated'} /></div>
      <p className="text-base leading-7 text-[var(--ink-900)]">{inspector.summary}</p>
      <DefinitionList items={[
        ['Source', inspector.source],
        ['Validated by', inspector.validatedBy || 'Not recorded'],
        ['Validated', formatDate(inspector.validatedAt)],
        ['Related risk', inspector.relatedRisk?.label || 'Not classified'],
        ['Action owner', inspector.relatedAction?.owner || 'Not assigned'],
      ]} />
      {!!inspector.blockingGates?.length && <Section title="Gate impact"><div className="flex flex-wrap gap-2">{inspector.blockingGates.map((gate) => <StatusBadge key={gate} value="needs_review" label={gate.replaceAll('_', ' ')} />)}</div></Section>}
      <Section title="Evidence">
        {inspector.evidenceRefs?.length ? inspector.evidenceRefs.map((evidence, index) => (
          <blockquote key={`${evidence.id || 'evidence'}-${index}`} className="border-t border-[var(--line)] py-3 first:border-t-0"><p className="text-sm leading-6">{evidence.quote || evidence.summary}</p><cite className="mt-1 block text-xs not-italic text-[var(--ink-500)]">{evidence.source_type || 'Approved evidence'}</cite></blockquote>
        )) : <p className="text-sm leading-6 text-[var(--ink-600)]">The aggregate validation record is available, but no raw excerpt is exposed in this Boardroom snapshot.</p>}
      </Section>
      <div className="flex flex-wrap gap-2 border-t border-[var(--line)] pt-5">
        {inspector.relatedAction && <Link to="/work?view=actions" className="tkp-btn-primary inline-flex items-center gap-2">Open action <ArrowSquareOut size={14} /></Link>}
        <Link to="/intelligence?view=signals" className="tkp-btn-secondary inline-flex items-center gap-2">Open signal stream <ArrowSquareOut size={14} /></Link>
      </div>
    </>
  );
}

function MetricDetails({ inspector, onSelect }) {
  return (
    <>
      <div className="font-heading text-4xl font-bold tracking-[-0.03em] text-[var(--ink-950)]">{inspector.value}</div>
      {inspector.summary && <p className="text-sm leading-6 text-[var(--ink-700)]">{inspector.summary}</p>}
      {inspector.type === 'metric' && (
        <Section title="Primary drivers">
          <div className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
            {inspector.drivers.map((risk) => <button key={risk.id} type="button" onClick={() => onSelect({ type: 'risk', id: risk.id })} className="flex w-full items-center justify-between gap-3 py-3 text-left"><span className="font-semibold">{risk.label}</span><SeverityBadge value={risk.level} /></button>)}
          </div>
        </Section>
      )}
      {inspector.type === 'posture' && (
        <>
          <Section title="Gate conditions"><div className="space-y-2">{inspector.conditions.map((condition) => <div key={condition.label} className="flex items-center justify-between gap-3 text-sm"><span>{condition.label}</span><StatusBadge value={condition.met ? 'ready' : 'needs_review'} label={condition.met ? 'Met' : 'Pending'} /></div>)}</div></Section>
          <Section title="Readiness dimensions"><div className="space-y-3">{inspector.readinessDimensions.map((item) => <div key={item.name} className="dim-row flex items-center justify-between gap-4 text-sm"><span className="font-semibold">{item.name}</span><span className="font-mono text-xs">{item.score} / 100</span></div>)}</div></Section>
        </>
      )}
      {inspector.type === 'attention' && <Section title="Validated decision-relevant risks"><div className="space-y-2">{inspector.relatedRisks.map((risk) => <button key={risk.id} type="button" onClick={() => onSelect({ type: 'risk', id: risk.id })} className="flex w-full items-center justify-between gap-3 border-t border-[var(--line)] py-3 text-left first:border-t-0"><span className="font-semibold">{risk.label}</span><SeverityBadge value={risk.level} /></button>)}</div></Section>}
    </>
  );
}

function EvidenceDetails({ inspector, onSelect }) {
  const climate = inspector.climate;
  return (
    <>
      <div className="font-heading text-4xl font-bold tracking-[-0.03em]">{inspector.value}</div>
      <p className="text-sm leading-6 text-[var(--ink-700)]">{inspector.summary}</p>
      {climate && <Section title="Aggregate execution conditions"><DefinitionList items={[
        ['Alignment', climate.alignment_level],
        ['Ownership clarity', climate.ownership_clarity_level],
        ['Engagement', climate.engagement_level],
        ['Uncertainty', climate.uncertainty_level],
        ['Friction', climate.friction_level],
        ['Decision pressure', climate.decision_pressure_level],
      ]} /></Section>}
      {inspector.relatedSignals && <Section title="Validation chain"><RelatedSignals signals={inspector.relatedSignals} onSelect={onSelect} /></Section>}
      <Link to="/intelligence?view=knowledge" className="tkp-btn-primary inline-flex items-center gap-2">Open provenance <ArrowSquareOut size={14} /></Link>
    </>
  );
}

function EventDetails({ inspector, onSelect }) {
  return (
    <>
      <div className="flex items-center gap-3"><Clock size={20} /><div className="font-heading text-3xl font-bold tracking-[-0.03em]">{inspector.value}</div></div>
      <p className="text-sm leading-6 text-[var(--ink-700)]">{inspector.summary}</p>
      <Section title="Related validated changes"><RelatedSignals signals={inspector.relatedSignals} onSelect={onSelect} /></Section>
    </>
  );
}

export default function BoardroomInspector({ inspector, onClose, onSelect }) {
  return (
    <DetailDrawer title={inspector?.title || 'Boardroom context'} open={Boolean(inspector)} onClose={onClose}>
      {inspector && (
        <div className="space-y-6" data-testid={`boardroom-inspector-${inspector.type}`}>
          {inspector.type === 'risk' && <RiskDetails inspector={inspector} onSelect={onSelect} />}
          {inspector.type === 'signal' && <SignalDetails inspector={inspector} />}
          {['metric', 'posture', 'attention'].includes(inspector.type) && <MetricDetails inspector={inspector} onSelect={onSelect} />}
          {inspector.type === 'evidence' && <EvidenceDetails inspector={inspector} onSelect={onSelect} />}
          {inspector.type === 'event' && <EventDetails inspector={inspector} onSelect={onSelect} />}
        </div>
      )}
    </DetailDrawer>
  );
}
